import { fetchAuthSession } from "aws-amplify/auth";
import { applyEdit, getProgressDeviceId, listPendingProgress, readDeviceProgress, reconcileDeviceSave } from "./deviceProgress";
import { getCloudProgressForSync, putCloudProgressConditional } from "./progressCloud";
import { plainRecord } from "./progressData";

// Completion/set and undo/remove apply to their item. Counts add to the latest
// cloud counts. Reset clears the latest feature, then later edits apply. For
// conflicting sets/notes, the last conditional write accepted by the server
// wins; device clock timestamps do not decide ordering.
export async function syncPendingProgress(userId: string, isActive: () => boolean): Promise<void> {
    const active = () => isActive() && navigator.onLine !== false;
    if (userId === "guest" || !active()) return;
    if (!(await listPendingProgress(userId)).length || !active()) return;
    const session = await fetchAuthSession({ forceRefresh: true });
    if (!active()) return;
    if (session.identityId !== userId || !session.credentials) throw new Error("Sign in to this account again to sync");
    const deviceId = await getProgressDeviceId();
    // Bounded work per pass; the coordinator schedules another pass if edits
    // keep arriving. A failed/ambiguous request leaves the queue untouched.
    for (let pass = 0; pass < 20 && active(); pass++) {
        const queues = await listPendingProgress(userId);
        if (!queues.length) return;
        for (const queue of queues) {
            if (!active()) return;
            let completed = false;
            for (let conflict = 0; conflict < 5 && active(); conflict++) {
                const snapshot = await readDeviceProgress(userId, queue.feature);
                const pending = snapshot.pending;
                if (!pending) { completed = true; break; }
                const remote = await getCloudProgressForSync(userId, queue.feature);
                if (!active()) return;
                const receipt = remote?.syncReceipts?.[deviceId];
                const confirmed = typeof receipt === "number" && Number.isSafeInteger(receipt) && receipt >= 0 ? receipt : 0;
                const edits = pending.edits.filter(edit => Number(edit.id.split(":")[0]) > confirmed);
                const cloudData = plainRecord(queue.feature, remote?.data);
                if (!edits.length) {
                    await reconcileDeviceSave(userId, queue.feature, confirmed, cloudData, remote?.updatedAt);
                    completed = true; break;
                }
                try {
                    const saved = await putCloudProgressConditional(userId, queue.feature, edits.reduce(applyEdit, cloudData), remote, deviceId, pending.revision, active);
                    // A completed write can safely acknowledge its own account
                    // even if the UI switched while the request was in flight.
                    await reconcileDeviceSave(userId, queue.feature, pending.revision, plainRecord(queue.feature, saved.data), saved.updatedAt);
                    completed = true; break;
                } catch (error) {
                    if ((error as { name?: string }).name !== "ConditionalCheckFailedException") throw error;
                }
            }
            if (!active()) return;
            if (!completed) throw new Error("Progress changed on another device; retry sync");
        }
    }
}
